import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberDto } from '@pokerroom/types';
import { safeFormatDate } from '../../../lib/format';
import { LevelBadge } from '../memberBadges';
import { MemberCrmProfile } from '../MemberCrmProfile';
import { MemberLinePanel } from '../MemberLinePanel';

/** 會員資訊: profile facts, portal controls, CRM source and custom fields. */
export function MemberInfoTab({ member }: { member: MemberDto }) {
  const { t } = useTranslation('member');
  const notSet = <span className="font-normal text-base-content/70">{t('detail.notSet')}</span>;

  return (
    <div>
      <h2 className="mb-3 font-semibold">{t('detail.profile')}</h2>
      <dl className="mb-6 grid grid-cols-1 gap-px border border-base-300 bg-base-300 sm:grid-cols-2">
        <Fact label={t('tier')}>
          {member.level ? <LevelBadge level={member.level} className="text-sm" /> : notSet}
        </Fact>
        <Fact label={t('phone')}>
          {member.phone ? <span className="tabular-nums">{member.phone}</span> : notSet}
        </Fact>
        <Fact label={t('email')}>{member.email || notSet}</Fact>
        <Fact label={t('createdAtLabel')}>
          <span className="tabular-nums">
            {safeFormatDate(member.createdAt, (d) => d.toLocaleDateString())}
          </span>
        </Fact>
      </dl>

      <MemberCrmProfile member={member} section="profile" />
      <div className="mb-4 flex gap-3 border border-base-300 bg-base-200 p-3 text-sm">
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="mt-0.5 h-4 w-4 shrink-0 text-primary"
        >
          <circle cx="10" cy="10" r="7.25" />
          <path d="M10 9v5M10 6.25v.5" strokeLinecap="square" />
        </svg>
        <div>
          <p className="font-medium text-base-content">{t('sharedProfileWarningTitle')}</p>
          <p className="text-base-content/70">{t('sharedProfileWarningBody')}</p>
        </div>
      </div>
      <MemberLinePanel memberId={member.id} />

      {member.note && (
        <div className="mb-6 p-3 bg-base-300 rounded-md text-sm text-base-content">
          <span className="text-base-content/70 mr-2">{t('noteLabel')}</span>
          {member.note}
        </div>
      )}

      {member.customValues && member.customValues.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-base-content mb-3">{t('customFields')}</h2>
          <dl className="grid grid-cols-1 gap-px border border-base-300 bg-base-300 sm:grid-cols-2">
            {member.customValues.map((cv) => (
              <Fact key={cv.fieldKey} label={cv.displayName}>
                {cv.value || notSet}
              </Fact>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 bg-base-100 px-3 py-2.5 [overflow-wrap:anywhere]">
      <dt className="mb-1 text-xs text-base-content/70">{label}</dt>
      <dd className="text-sm font-medium text-base-content">{children}</dd>
    </div>
  );
}
